import { LockClosedIcon } from "@heroicons/react/20/solid";
import { useLanguage } from "../../../contexts/LanguageContext";
import { TAG_LABEL_KEYS, connectedSummary } from "../providers";
import { ProviderLogo, outlineButtonClass } from "./ui";

export function TagChip({ tag }) {
  const { t } = useLanguage();
  const labelKey = TAG_LABEL_KEYS[tag];
  return (
    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-700">
      {labelKey ? t(`integrations.tags.${labelKey}`) : tag}
    </span>
  );
}

// A provider the store has connected: what it does right now, and Configure.
export function ConnectedCard({ provider, integration, onConfigure }) {
  const { t } = useLanguage();
  return (
    <article
      aria-label={provider.name}
      className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-1 rounded-[14px] border border-emerald-200 bg-white px-[18px] py-4 [grid-template-areas:'tile_name_btn'_'tile_desc_desc']"
    >
      <span className="self-start [grid-area:tile]">
        <ProviderLogo provider={provider} />
      </span>
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 [grid-area:name]">
        <span className="text-[15px] font-semibold text-gray-900">{provider.name}</span>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
          <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />
          {t("integrations.connected")}
        </span>
      </span>
      <span className="text-[13px] leading-snug break-words text-gray-600 [grid-area:desc]">
        {connectedSummary(integration, t)}
      </span>
      <button
        type="button"
        onClick={onConfigure}
        aria-label={t("integrations.configureLabel", { name: provider.name })}
        className={`${outlineButtonClass} [grid-area:btn]`}
      >
        {t("integrations.configure")}
      </button>
    </article>
  );
}

// A provider the store can connect. `locked`: the plan doesn't include
// integrations, so Connect opens the plan dialog instead.
export function AvailableCard({ provider, locked, onConnect }) {
  const { t } = useLanguage();
  return (
    <article
      aria-label={provider.name}
      className="flex flex-col gap-3 rounded-[14px] border border-gray-200 bg-white px-[18px] py-4"
    >
      <div className="flex items-center gap-3">
        <ProviderLogo provider={provider} />
        <span className="flex flex-col gap-1">
          <span className="text-[15px] font-semibold text-gray-900">{provider.name}</span>
          <span className="flex flex-wrap gap-1">
            {provider.tags.map((tag) => (
              <TagChip key={tag} tag={tag} />
            ))}
          </span>
        </span>
      </div>
      <p className="flex-1 text-[13px] leading-normal text-gray-600">
        {t(`integrations.description.${provider.key}`)}
      </p>
      {provider.soon ? (
        <span className="self-start rounded-lg bg-gray-100 px-2.5 py-1.5 text-xs font-semibold text-gray-600">
          {t("integrations.comingSoon")}
        </span>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onConnect}
            aria-label={t("integrations.connectLabel", { name: provider.name })}
            className={`${outlineButtonClass} px-4`}
          >
            {t("integrations.connect")}
          </button>
          {locked && (
            <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-[3px] text-[11px] font-semibold text-indigo-700">
              <LockClosedIcon className="h-3 w-3" aria-hidden="true" />
              {t("integrations.locked.badge")}
            </span>
          )}
        </div>
      )}
    </article>
  );
}
